import { useEffect, useRef, useState } from 'react'

const TOP_RESET_PX = 24
const HIDE_DELTA_PX = 12
const SHOW_DELTA_PX = 6

function isEditableElement(el: Element | null): boolean {
  if (!el) return false
  if (el instanceof HTMLTextAreaElement) return true
  if (el instanceof HTMLSelectElement) return true
  if (el instanceof HTMLInputElement) {
    return ![
      'button',
      'checkbox',
      'color',
      'file',
      'radio',
      'range',
      'reset',
      'submit',
    ].includes(el.type)
  }
  return el instanceof HTMLElement && el.isContentEditable
}

export function useScrollUI() {
  const [isUIHidden, setIsUIHidden] = useState(false)
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false)
  const hiddenRef = useRef(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    // Touch-first devices only — mouse users don't need the screen-space optim
    // and a moving header feels buggy on desktop.
    if (!window.matchMedia('(pointer: coarse)').matches) return

    let lastY = Math.max(window.scrollY || 0, 0)
    let downDelta = 0
    let upDelta = 0

    const updateHidden = (next: boolean) => {
      if (hiddenRef.current === next) return
      hiddenRef.current = next
      setIsUIHidden(next)
    }

    const onScroll = () => {
      const y = Math.max(window.scrollY || 0, 0)
      const dy = y - lastY
      lastY = y

      if (y <= TOP_RESET_PX) {
        downDelta = 0
        upDelta = 0
        updateHidden(false)
        return
      }

      if (Math.abs(dy) < 1) return

      if (dy > 0) {
        downDelta += dy
        upDelta = 0
        if (downDelta >= HIDE_DELTA_PX) {
          updateHidden(true)
          downDelta = 0
        }
        return
      }

      upDelta += -dy
      downDelta = 0
      if (upDelta >= SHOW_DELTA_PX) {
        updateHidden(false)
        upDelta = 0
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (typeof document === 'undefined') return

    const syncKeyboardState = () => {
      setIsKeyboardOpen(isEditableElement(document.activeElement))
    }

    const onFocusChange = () => {
      window.requestAnimationFrame(syncKeyboardState)
    }

    document.addEventListener('focusin', onFocusChange)
    document.addEventListener('focusout', onFocusChange)
    return () => {
      document.removeEventListener('focusin', onFocusChange)
      document.removeEventListener('focusout', onFocusChange)
    }
  }, [])

  return {
    isUIHidden,
    isKeyboardOpen,
  }
}
